import styled from '@emotion/styled';
import { AlertTriangle, CheckCircle, Info, X, XCircle } from 'lucide-react';
import type React from 'react';
import { borderRadius, colors, elevation, spacing, typography } from '@/lib/design-tokens';
import type { ToastType } from '@/stores/toast-store';

interface ToastProps {
  type: ToastType;
  title: string;
  message?: string;
  onClose?: () => void;
}

const BORDER_COLORS: Record<ToastType, string> = {
  success: colors.green[700],
  error: colors.red[500],
  warning: colors.yellow[400],
  info: colors.brand.blue[600],
};

const ICON_COLORS: Record<ToastType, string> = {
  success: colors.green[700],
  error: colors.red[500],
  warning: colors.yellow[400],
  info: colors.brand.blue[600],
};

const ICONS: Record<ToastType, React.ElementType> = {
  success: CheckCircle,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
};

const ToastContainer = styled.div<{ toastType: ToastType }>`
  display: flex;
  align-items: flex-start;
  gap: ${spacing[250]};

  min-width: 320px;
  max-width: 480px;
  padding: ${spacing[300]};

  background-color: ${colors.neutral[0]};
  border-radius: ${borderRadius.default};
  border-left: 4px solid ${({ toastType }) => BORDER_COLORS[toastType]};

  box-shadow: ${elevation.overflow};
`;

const IconWrapper = styled.div<{ toastType: ToastType }>`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  color: ${({ toastType }) => ICON_COLORS[toastType]};
`;

const Content = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.div`
  font-family: ${typography.fontFamily.default};
  font-size: ${typography.fontSize.body};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.neutral[900]};
  line-height: ${typography.lineHeight.normal};
`;

const Message = styled.div`
  font-family: ${typography.fontFamily.default};
  font-size: ${typography.fontSize.body};
  color: ${colors.neutral[700]};
  line-height: ${typography.lineHeight.normal};
  margin-top: ${spacing[100]};
`;

const CloseButton = styled.button`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  background: transparent;
  border: none;
  border-radius: ${borderRadius.default};
  color: ${colors.neutral[600]};
  cursor: pointer;

  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(9, 30, 66, 0.06);
  }

  &:focus-visible {
    outline: 2px solid ${colors.brand.blue[300]};
    outline-offset: 2px;
  }
`;

function Toast({ type, title, message, onClose }: ToastProps) {
  const Icon = ICONS[type];

  return (
    <ToastContainer toastType={type} role="status">
      <IconWrapper toastType={type}>
        <Icon size={20} />
      </IconWrapper>
      <Content>
        <Title>{title}</Title>
        {message && <Message>{message}</Message>}
      </Content>
      {onClose && (
        <CloseButton onClick={onClose} aria-label="通知を閉じる">
          <X size={16} />
        </CloseButton>
      )}
    </ToastContainer>
  );
}

export type { ToastProps };
export { Toast };
